import { useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { toast } from 'react-toastify'
import { APP } from '@/constants'
import { services, contactTimes, process, trustSignals } from '@/data/contact'
import { CustomSelect } from '@/components/ui/CustomSelect'
import { sendContactEmail } from '@/services/contactEmail'
import {
  contactFieldLimits,
  emptyContactForm,
  validateContactForm,
  type ContactFormData,
  type ContactFormErrors,
} from '@/utils/contactForm'

const inputClass =
  'h-12 px-4 rounded-xl border border-base-300 bg-white text-base text-base-900 placeholder:text-base-500 focus:outline-none focus:border-green-700 focus:ring-2 focus:ring-green-700/30 transition-colors w-full'
const labelClass = 'text-xs font-medium text-base-700 tracking-[0.12em] uppercase mb-2 block'

export const ContactForm = () => {
  const [searchParams] = useSearchParams()
  const requestedService = searchParams.get('service') ?? ''
  const validService = services.some((service) => service.value === requestedService)
    ? requestedService
    : ''
  const [form, setForm] = useState<ContactFormData>({
    ...emptyContactForm,
    service: validService,
  })
  const [submitted, setSubmitted] = useState(false)
  const [errors, setErrors] = useState<ContactFormErrors>({})
  const [sendError, setSendError] = useState('')
  const [isSending, setIsSending] = useState(false)
  const sendingRef = useRef(false)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (sendingRef.current) return

    const nextErrors = validateContactForm(form)
    setErrors(nextErrors)
    setSendError('')
    const firstInvalidField = Object.keys(nextErrors)[0]
    if (firstInvalidField) {
      const selector =
        firstInvalidField === 'service'
          ? '[aria-label="Service of interest"]'
          : firstInvalidField === 'preferredTime'
            ? '[aria-label="Preferred contact time"]'
            : `[name="${firstInvalidField}"]`
      e.currentTarget.querySelector<HTMLElement>(selector)?.focus()
      return
    }

    sendingRef.current = true
    setIsSending(true)
    try {
      await toast.promise(
        sendContactEmail(form),
        {
          pending: 'Sending your enquiry…',
          success: 'Your enquiry has been sent successfully. Thank you!',
          error: {
            render({ data }) {
              return data instanceof Error
                ? data.message
                : 'We could not send your enquiry. Please try again or email us directly.'
            },
          },
        },
        { autoClose: 6000, closeButton: true },
      )
      setSubmitted(true)
    } catch (error) {
      setSendError(
        error instanceof Error
          ? error.message
          : 'We could not send your enquiry. Please try again or email us directly below.',
      )
    } finally {
      sendingRef.current = false
      setIsSending(false)
    }
  }

  const setField = (field: keyof ContactFormData, value: string) => {
    setForm((previous) => ({ ...previous, [field]: value }))
    setErrors((previous) => ({ ...previous, [field]: undefined }))
  }

  const set =
    (field: keyof ContactFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setField(field, e.target.value)

  const fieldError = (field: keyof ContactFormData) =>
    errors[field] ? (
      <p id={`contact-${field}-error`} className="mt-2 text-sm text-red-600">
        {errors[field]}
      </p>
    ) : null

  if (submitted) {
    return (
      <section className="min-h-[60vh] bg-base-50 flex items-center justify-center py-24 px-6">
        <div className="max-w-md text-center" role="status">
          <div className="w-14 h-14 rounded-full bg-green-100 border border-green-200 flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-6 h-6 text-green-600"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h2 className="font-display text-[1.8rem] text-base-900 leading-tight tracking-tight mb-4">
            Enquiry Received
          </h2>
          <p className="text-base-500 text-[0.88rem] leading-relaxed mb-3">
            Thank you, {form.firstName.trim()}. Your enquiry has been sent to Kandake. She will
            review your message and respond within 24–48 hours.
          </p>
          <p className="text-base-400 text-[0.78rem] leading-relaxed">
            If you have an urgent question, you can also reach us directly at{' '}
            <a href={`mailto:${APP.CONTACT_EMAIL}`} className="text-green-700 hover:underline">
              {APP.CONTACT_EMAIL}
            </a>
          </p>
        </div>
      </section>
    )
  }

  return (
    <section className="bg-base-50 py-16 sm:py-20 lg:py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 lg:gap-16 items-start">
          {/* Form */}
          <div className="rounded-3xl border border-base-200 bg-white p-6 sm:p-9">
            <form onSubmit={handleSubmit} noValidate aria-busy={isSending}>
              {Object.values(errors).some(Boolean) && (
                <p role="alert" className="mb-6 text-sm text-red-600">
                  Please check the highlighted fields before sending your enquiry.
                </p>
              )}
              {sendError && (
                <p
                  role="alert"
                  className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
                >
                  {sendError}
                </p>
              )}
              <fieldset disabled={isSending} className="min-w-0 space-y-6">
                <legend className="sr-only">Your enquiry details</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-firstName" className={labelClass}>
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="contact-firstName"
                      name="firstName"
                      autoComplete="given-name"
                      maxLength={contactFieldLimits.firstName}
                      aria-invalid={Boolean(errors.firstName)}
                      aria-describedby={errors.firstName ? 'contact-firstName-error' : undefined}
                      type="text"
                      value={form.firstName}
                      onChange={set('firstName')}
                      placeholder="Your first name"
                      required
                      className={inputClass}
                    />
                    {fieldError('firstName')}
                  </div>
                  <div>
                    <label htmlFor="contact-lastName" className={labelClass}>
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="contact-lastName"
                      name="lastName"
                      autoComplete="family-name"
                      maxLength={contactFieldLimits.lastName}
                      aria-invalid={Boolean(errors.lastName)}
                      aria-describedby={errors.lastName ? 'contact-lastName-error' : undefined}
                      type="text"
                      value={form.lastName}
                      onChange={set('lastName')}
                      placeholder="Your last name"
                      required
                      className={inputClass}
                    />
                    {fieldError('lastName')}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-email" className={labelClass}>
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      autoComplete="email"
                      maxLength={contactFieldLimits.email}
                      aria-invalid={Boolean(errors.email)}
                      aria-describedby={errors.email ? 'contact-email-error' : undefined}
                      type="email"
                      value={form.email}
                      onChange={set('email')}
                      placeholder="your@email.com"
                      required
                      className={inputClass}
                    />
                    {fieldError('email')}
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className={labelClass}>
                      Phone Number
                    </label>
                    <input
                      id="contact-phone"
                      name="phone"
                      autoComplete="tel"
                      maxLength={contactFieldLimits.phone}
                      aria-invalid={Boolean(errors.phone)}
                      aria-describedby={errors.phone ? 'contact-phone-error' : undefined}
                      type="tel"
                      value={form.phone}
                      onChange={set('phone')}
                      placeholder="+44 7700 000000"
                      className={inputClass}
                    />
                    {fieldError('phone')}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelClass}>
                      Service of Interest <span className="text-red-500">*</span>
                    </label>
                    <CustomSelect
                      value={form.service}
                      onChange={(value) => setField('service', value)}
                      options={services.filter((service) => service.value)}
                      label="Service of interest"
                      placeholder="Select a service"
                      required
                      error={errors.service}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Preferred Contact Time</label>
                    <CustomSelect
                      value={form.preferredTime}
                      onChange={(value) => setField('preferredTime', value)}
                      options={contactTimes}
                      label="Preferred contact time"
                      placeholder="No preference"
                      error={errors.preferredTime}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-message" className={labelClass}>
                    Your Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    maxLength={contactFieldLimits.message}
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={
                      errors.message ? 'contact-message-error contact-privacy' : 'contact-privacy'
                    }
                    value={form.message}
                    onChange={set('message')}
                    rows={6}
                    required
                    placeholder="Tell Kandake which service you are interested in, your goals, or any general questions..."
                    className="px-4 py-3 rounded-xl border border-base-300 bg-white text-base text-base-900 placeholder:text-base-500 focus:outline-none focus:border-green-700 focus:ring-2 focus:ring-green-700/30 transition-colors resize-none w-full"
                  />
                  {fieldError('message')}
                  <p id="contact-privacy" className="mt-3 text-sm leading-6 text-base-600">
                    Your contact details and message are sent to Kandake via EmailJS. Please do not
                    include sensitive medical information; you can discuss that directly with
                    Kandake.
                  </p>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isSending}
                    className="min-h-12 w-full rounded-full bg-green-700 px-10 text-sm font-medium text-white transition-colors hover:bg-green-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-700 focus-visible:ring-offset-4 disabled:cursor-wait disabled:opacity-60 sm:w-auto"
                  >
                    {isSending ? 'Sending enquiry…' : 'Send Enquiry'}
                  </button>
                  <p role="status" className="sr-only">
                    {isSending ? 'Sending your enquiry. Please wait.' : ''}
                  </p>
                  <p className="mt-4 text-sm text-base-600">
                    Or email directly:{' '}
                    <a
                      href={`mailto:${APP.CONTACT_EMAIL}`}
                      className="text-green-700 hover:underline"
                    >
                      {APP.CONTACT_EMAIL}
                    </a>
                  </p>
                </div>
              </fieldset>
            </form>
          </div>

          {/* Info sidebar */}
          <div className="space-y-6">
            <div className="rounded-3xl border border-base-200 bg-white p-6">
              <h2 className="mb-2 font-display text-xl text-base-900">What happens next</h2>
              <ol>
                {process.map((p, i) => (
                  <li
                    key={p.n}
                    className={`flex gap-4 py-5 ${i < process.length - 1 ? 'border-b border-base-200' : ''}`}
                  >
                    <span className="font-display text-sm text-gold-500 shrink-0 pt-0.5">
                      {p.n}
                    </span>
                    <div>
                      <h3 className="font-display text-lg text-base-900 mb-1">{p.title}</h3>
                      <p className="text-sm text-base-600 leading-6">{p.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="p-6 rounded-3xl bg-green-50 border border-green-100 space-y-3">
              {trustSignals.map((t) => (
                <div key={t} className="flex items-center gap-3">
                  <span className="w-5 h-5 rounded-full bg-white flex items-center justify-center shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-700" />
                  </span>
                  <p className="text-sm text-green-900">{t}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
